
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export const useInterfaceModel = (businessTypeId: string | undefined) => {
  return useQuery({
    queryKey: ["businessInterface", businessTypeId],
    queryFn: async () => {
      if (!businessTypeId) {
        console.log("No business type ID provided");
        return null;
      }
      
      console.log("Fetching interface model for business type:", businessTypeId);
      
      // Récupérer d'abord le type d'entreprise avec son modèle d'interface par défaut
      const { data: businessType, error: businessTypeError } = await supabase
        .from("business_types")
        .select(`
          *,
          interface_models!business_types_default_interface_model_id_fkey (
            id,
            name,
            description,
            layout,
            features,
            menu_items,
            settings,
            business_type_id,
            business_types!interface_models_business_type_id_fkey (
              id,
              name,
              description
            )
          )
        `)
        .eq("id", businessTypeId)
        .single();

      if (businessTypeError) {
        console.error("Error fetching business type:", businessTypeError);
        toast.error("Erreur lors du chargement du type d'entreprise");
        throw businessTypeError;
      }

      if (!businessType) {
        console.error("Business type not found:", businessTypeId);
        toast.error("Type d'entreprise non trouvé");
        return null;
      }

      // Si un modèle d'interface par défaut existe, le retourner
      if (businessType.interface_models) {
        console.log("Using default interface model:", businessType.interface_models);
        return businessType.interface_models;
      }

      // Sinon, chercher le modèle d'interface le plus récent pour ce type d'entreprise
      const { data: modelData, error: modelError } = await supabase
        .from("interface_models")
        .select(`
          *,
          business_types!interface_models_business_type_id_fkey (
            id,
            name,
            description
          )
        `)
        .eq("business_type_id", businessTypeId)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (modelError) {
        console.error("Error fetching interface model:", modelError);
        toast.error("Erreur lors du chargement de l'interface");
        throw modelError;
      }

      if (!modelData) {
        console.log("No interface model found, creating default model");
        const defaultModel = {
          id: 'default',
          business_type_id: businessTypeId,
          name: businessType.name,
          description: businessType.description || "Interface par défaut",
          business_types: businessType,
          layout: {
            sections: [
              {
                name: "Gestion des stocks",
                features: [
                  {
                    name: "Inventaire",
                    description: "Gérez votre inventaire en temps réel"
                  },
                  {
                    name: "Ventes",
                    description: "Enregistrez vos ventes"
                  },
                  {
                    name: "Approvisionnement",
                    description: "Gérez vos approvisionnements"
                  }
                ]
              }
            ]
          }
        };
        return defaultModel;
      }

      console.log("Retrieved interface model:", modelData);
      return modelData;
    },
    enabled: !!businessTypeId,
  });
};
